// src/components/story/CreateStoryModal.jsx
import React, { useState, useCallback, useRef, useEffect, Suspense } from 'react';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));
import { Rnd } from 'react-rnd';
import { useStoriesStore, useDataStore } from '../../stores/useDataStore'; // 🟢 Fix: Add useDataStore
import { useModalStore } from '../../stores/useUIStore'; // 🟢 2. Importamos el store de modales

const CreateStoryModal = () => {
  // 🟢 3. Obtenemos las acciones de los stores de Zustand
  const addStory = useDataStore((state) => state.addStory); // 🟢 Fix: useDataStore no useStoriesStore
  const modals = useModalStore((state) => state.modals);
  const closeModal = useModalStore((state) => state.closeModal);
  const isOpen = modals.createStory.isOpen;

  // 🟢 SOLUCIÓN: Refactorizamos para manejar múltiples escenas
    const [scenes, setScenes] = useState([]);
    const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
    const [isPublishing, setIsPublishing] = useState(false);
    const [activeTool, setActiveTool] = useState(null);
    const [privacy, setPrivacy] = useState('public');
  
    // State for managing text input for new text elements
    const [newText, setNewText] = useState('');
    const [newTextColor, setNewTextColor] = useState('#ffffff');
    const [newFontSize, setNewFontSize] = useState('24px');
    const [newFontFamily, setNewFontFamily] = useState('Arial');
    const [newTextAlign, setNewTextAlign] = useState('center');
  
    // Predefined professional color palette
    const professionalBackgrounds = [
      'bg-gradient-to-br from-gray-800 to-gray-900',
      'bg-gradient-to-br from-blue-700 to-blue-900',
      'bg-gradient-to-br from-purple-700 to-indigo-900',
      'bg-gradient-to-br from-green-700 to-teal-900',
      'bg-gradient-to-br from-red-700 to-pink-900',
      'bg-gradient-to-br from-yellow-700 to-orange-900',
      'bg-gradient-to-br from-gray-600 to-gray-800',
      'bg-gradient-to-br from-blue-400 to-purple-600',
      'bg-gradient-to-br from-green-400 to-blue-600',
      'bg-gradient-to-br from-pink-400 to-red-600',
    ];
  
    useEffect(() => {
      if (isOpen && scenes.length === 0) {
        setScenes([{
          file: null,
          preview: null,
          type: 'text',
          background: professionalBackgrounds[0], // Default to a professional background
          duration: 5000,
          textElements: [],
          emojiElements: [],
        }]);
        setCurrentSceneIndex(0);
      }
    }, [isOpen]);
  
    const fileInputRef = useRef(null);

    const onDrop = useCallback((acceptedFiles) => {
      const newScenes = acceptedFiles.map(file => ({
        file: file,
        preview: URL.createObjectURL(file),
        type: file.type.startsWith('image') ? 'image' : 'video',
        background: '', // No background for image/video
        duration: file.type.startsWith('image') ? 5000 : 15000,
        textElements: [],
        emojiElements: [],
      }));
  
      setScenes(prev => {
        // If there's only one scene and it's empty (text-only, no custom content), replace it.
        // Otherwise, add new scenes.
        if (prev.length === 1 && !prev[0].file && prev[0].textElements.length === 0 && prev[0].emojiElements.length === 0) {
          return newScenes;
        }
        return [...prev, ...newScenes];
      });
  
      // Correctly set current scene index if starting with an empty slate
      if (scenes.length === 0 && newScenes.length > 0) {
        setCurrentSceneIndex(0);
      }
    }, [scenes]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      noClick: true, // Siempre deshabilitamos el click del root, usaremos nuester propio o el del botón
      noDrag: true, // Siempre deshabilitamos la acción de drag and drop en el contenedor principal
      accept: { 'image/*': [], 'video/*': [] },
    });
  
    const updateCurrentScene = (props) => {
      setScenes(prev => 
        prev.map((s, i) => 
          i === currentSceneIndex ? { ...s, ...props } : s
        )
      );
    };

    const addTextElement = () => {
      if (!newText.trim()) return;
      const newElement = {
        id: `text-${Date.now()}`,
        text: newText,
        x: 50, y: 50, // Default position
        width: 200, height: 50, // Default size
        color: newTextColor,
        fontSize: newFontSize,
        fontFamily: newFontFamily,
        textAlign: newTextAlign,
      };
      updateCurrentScene({ textElements: [...(currentScene.textElements || []), newElement] });
      setNewText(''); // Clear input after adding
    };
  
    const updateTextElement = (id, updates) => {
      updateCurrentScene({
        textElements: (currentScene.textElements || [])
          .map(el => (el && el.id === id ? { ...el, ...updates } : el))
          .filter(Boolean)
      });
    };
  
    const removeTextElement = (id) => {
      updateCurrentScene({
        textElements: (currentScene.textElements || []).filter(el => el && el.id !== id)
      });
    };
  
    const addEmojiElement = (emojiData) => {
      const newElement = {
        id: `emoji-${Date.now()}`,
        emoji: emojiData.emoji,
        x: 50, y: 50, // Default position
        size: '48px', // Default size
      };
      updateCurrentScene({ emojiElements: [...(currentScene.emojiElements || []), newElement] });
    };
  
    const updateEmojiElement = (id, updates) => {
      updateCurrentScene({
        emojiElements: (currentScene.emojiElements || [])
          .map(el => (el && el.id === id ? { ...el, ...updates } : el))
          .filter(Boolean)
      });
    };
  
    const removeEmojiElement = (id) => {
      updateCurrentScene({
        emojiElements: (currentScene.emojiElements || []).filter(el => el && el.id !== id)
      });
    };
  
    const removeScene = (indexToRemove) => {
      setScenes(prev => prev.filter((_, index) => index !== indexToRemove));
      if (currentSceneIndex >= indexToRemove) {
        setCurrentSceneIndex(prev => Math.max(0, prev - 1));
      }
    };
  
    const handlePublish = async () => {
      if (scenes.length === 0) {
        toast.error('Añade al menos una imagen, video o texto.');
        return;
      }
      setIsPublishing(true);
  
      const formData = new FormData();
  
      const scenesMetadata = scenes.map(scene => {
        const { file, preview, ...metadata } = scene;
        // Ensure textElements and emojiElements are included
        return {
          ...metadata,
          textElements: scene.textElements || [],
          emojiElements: scene.emojiElements || [],
        };
      });
  
      formData.append('scenesMetadata', JSON.stringify(scenesMetadata));
  
      scenes.forEach(scene => {
        if (scene.file) {
          formData.append('scenes', scene.file);
        }
      });
  
      formData.append('privacy', privacy);
  
      const result = await addStory(formData);
      setIsPublishing(false);
  
      if (result.success) {
        handleClose();
      }
    };
  
    const handleClose = () => {
      setScenes([]);
      setCurrentSceneIndex(0);
      setIsPublishing(false);
      setNewText('');
      setNewTextColor('#ffffff');
      setNewFontSize('24px');
      setNewFontFamily('Arial');
      setNewTextAlign('center');
      closeModal('createStory');
    };
  
    if (!isOpen) return null;
  
    const currentScene = scenes[currentSceneIndex];
  
    return (
      <div
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <div
          className="bg-gray-800 rounded-xl shadow-2xl flex w-full  top-24 -mt-12 max-w-6xl relative h-[70vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Columna Izquierda: Vista Previa y Edición */}
          <div className="flex-1 flex flex-col items-center justify-center p-4 bg-black relative">
            <div className={`relative w-full h-full rounded-lg shadow-xl overflow-hidden flex items-center justify-center text-center transition-all ${!currentScene?.file && currentScene?.background ? currentScene.background : 'bg-black'}`}>

              {currentScene?.preview && (
                <img
                  src={currentScene.preview}
                  alt="Vista previa"
                  className="w-full h-full object-contain absolute inset-0"
                />
              )}
              
              {!currentScene?.file && currentScene?.textElements.length === 0 && currentScene?.emojiElements.length === 0 && (
                <div className="text-white/70 flex flex-col items-center z-10">
                  <i className="fas fa-photo-video text-5xl"></i>
                  <p className="mt-4 font-semibold">Crea una historia con texto o añade una imagen/video</p>
                </div>
              )}
  
              {/* Render Text Elements */}
              {currentScene?.textElements?.map(el => {
                console.log("Text Element:", { id: el.id, text: el.text, x: el.x, y: el.y, width: el.width, height: el.height, color: el.color, fontSize: el.fontSize });
                return (
                <Rnd
                  key={el.id}
                  size={{ width: el.width, height: el.height }}
                  position={{ x: el.x, y: el.y }}
                  onDragStop={(e, d) => {
                    updateTextElement(el.id, { x: d.x, y: d.y });
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    updateTextElement(el.id, {
                      width: ref.offsetWidth,
                      height: ref.offsetHeight,
                      x: position.x,
                      y: position.y,
                    });
                  }}
                  bounds="parent"
                  className="border border-dashed border-red-500 hover:border-blue-500 cursor-grab"
                >
                  <div
                    style={{
                      color: el.color,
                      fontSize: el.fontSize,
                      fontFamily: el.fontFamily,
                      textAlign: el.textAlign,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: el.textAlign === 'left' ? 'flex-start' : el.textAlign === 'right' ? 'flex-end' : 'center',
                      wordBreak: 'break-word',
                      padding: '5px',
                    }}
                    className="relative"
                  >
                    {el.text}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeTextElement(el.id); }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      &times;
                    </button>
                  </div>
                </Rnd>
              )})}
  
              {/* Render Emoji Elements */}
              {currentScene?.emojiElements?.map(el => {
                console.log("Emoji Element:", el);
                return (
                <Rnd
                  key={el.id}
                  size={{ width: parseInt(el.size), height: parseInt(el.size) }}
                  position={{ x: el.x, y: el.y }}
                  onDragStop={(e, d) => {
                    updateEmojiElement(el.id, { x: d.x, y: d.y });
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    updateEmojiElement(el.id, {
                      size: `${ref.offsetWidth}px`,
                      x: position.x,
                      y: position.y,
                    });
                  }}
                  bounds="parent"
                  lockAspectRatio={true}
                  className="border border-dashed border-red-500 hover:border-blue-500 cursor-grab flex items-center justify-center"
                >
                  <span style={{ fontSize: el.size }}>{el.emoji}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeEmojiElement(el.id); }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                  >
                    &times;
                  </button>
                </Rnd>
              )})}
            </div>
            {/* Preview Slider */}
            {scenes.length > 0 && (
              <div className="w-full mt-4">
                <swiper-container slides-per-view="auto" space-between="8">
                  {scenes.map((scene, index) => (
                    <swiper-slide key={index} style={{ width: '80px' }} className="relative group">
                      <div
                        onClick={() => setCurrentSceneIndex(index)}
                        className={`w-20 h-20 object-cover rounded-md cursor-pointer border-2 ${index === currentSceneIndex ? 'border-blue-500' : 'border-transparent'} overflow-hidden flex items-center justify-center ${!scene.file && scene.background ? scene.background : ''}`}
                      >
                        {scene.preview && (
                          <img
                            src={scene.preview}
                            alt={`preview ${index}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {!scene.file && scene.textElements.length > 0 && (
                          <span className="text-white text-xs p-1 truncate">{scene.textElements[0].text}</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeScene(index); }}
                        className="absolute top-0 right-0 bg-black bg-opacity-60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      >
                        &times;
                      </button>
                    </swiper-slide>
                  ))}
                </swiper-container>
              </div>
            )}
          </div>
  
          {/* Columna Derecha: Controles */}
          <div className="w-1/3 bg-gray-900 p-4 flex flex-col overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Editar Escena</h3>
            
            {currentScene && (
              <div className="flex-grow overflow-y-auto space-y-4 pr-2">
                <div className="flex justify-around bg-gray-800 p-1 rounded-lg mb-4">
                  <button onClick={() => setActiveTool(activeTool === 'text' ? null : 'text')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${activeTool === 'text' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>
                    Texto
                  </button>
                  <button onClick={() => setActiveTool(activeTool === 'emoji' ? null : 'emoji')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${activeTool === 'emoji' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>
                    Emoji
                  </button>
                  {!currentScene.file && (
                    <button onClick={() => setActiveTool(activeTool === 'background' ? null : 'background')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${activeTool === 'background' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>
                      Fondo
                    </button>
                  )}
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => onDrop(Array.from(e.target.files))}
                  multiple
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="w-full bg-green-500 text-white py-2 rounded-md hover:bg-green-600 mb-4"
                >
                  Añadir Escena de Imagen/Video
                </button>
  
                {activeTool === 'text' && (
                  <div className="space-y-3">
                    <textarea 
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      className="w-full bg-gray-700 text-white border-gray-600 rounded-md p-2" 
                      placeholder="Añadir nuevo texto..." 
                    />
                    <div className="flex items-center gap-2">
                      <label className="text-white text-sm">Color:</label>
                      <input
                        type="color"
                        value={newTextColor}
                        onChange={(e) => setNewTextColor(e.target.value)}
                        className="w-8 h-8 rounded-full cursor-pointer"
                      />
                      <label className="text-white text-sm">Tamaño:</label>
                      <select
                        value={newFontSize}
                        onChange={(e) => setNewFontSize(e.target.value)}
                        className="bg-gray-700 text-white rounded-md p-1 text-sm"
                      >
                        <option value="16px">Pequeño</option>
                        <option value="24px">Mediano</option>
                        <option value="32px">Grande</option>
                        <option value="48px">Muy Grande</option>
                      </select>
                      <label className="text-white text-sm">Alineación:</label>
                      <select
                        value={newTextAlign}
                        onChange={(e) => setNewTextAlign(e.target.value)}
                        className="bg-gray-700 text-white rounded-md p-1 text-sm"
                      >
                        <option value="left">Izquierda</option>
                        <option value="center">Centro</option>
                        <option value="right">Derecha</option>
                      </select>
                    </div>
                    <button onClick={addTextElement} className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600">Añadir Texto</button>
                  </div>
                )}
  
                {activeTool === 'emoji' && (
                  <div className="relative">
                    <Suspense fallback={<div className="w-full h-96 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>}>
                      <EmojiPicker onEmojiClick={addEmojiElement} width="100%" />
                    </Suspense>
                  </div>
                )}
  
                {activeTool === 'background' && (
                  <div className="flex flex-wrap gap-2">
                    {professionalBackgrounds.map(bg => (
                      <button 
                        key={bg}
                        onClick={() => updateCurrentScene({ background: bg })}
                        className={`w-10 h-10 rounded-md border-2 ${bg} ${currentScene.background === bg ? 'border-blue-500' : 'border-transparent'}`}
                      >
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="pt-4 border-t border-gray-700 mt-auto">
              <button onClick={handlePublish} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 shadow-lg">
                {isPublishing ? 'Publicando...' : `Publicar ${scenes.length} Escena(s)`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
}

export default CreateStoryModal;
